import fire from '@store/firebase';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { CollectionPath } from '@typings/collections.enum';
import {
  IMyBettingBet,
  IBettingItem,
} from '@typings/betting/betting.interface';
import { saveWalletHistory } from '@store/firebase/setting/save-member-history';
import { updateUserWallet } from '@store/firebase/user/updateUser';
import { WalletHistoryType } from '../user/addWalletHistoryByType';

const firestore = fire.firestore();

// Topic CRUD -------------------------------------------------------------
export async function createOrUpdateIBettingItem(
  topic: IBettingItem & { id?: string },
  customCollectionPath?: string,
) {
  const ref = firestore.collection(
    customCollectionPath || CollectionPath.BETTING,
  );
  const now = Date.now();
  const data = {
    ...topic,
    createdAt: topic.createdAt || now,
    updatedAt: now,
  } as IBettingItem;
  if (topic.id) {
    await ref.doc(topic.id).set(data, { merge: true });
    return topic.id;
  } else {
    const docRef = await ref.add(data);
    return docRef.id;
  }
}

export async function deleteBettingItem(
  topicId: string,
  customCollectionPath?: string,
) {
  if (!topicId) throw new Error('삭제할 베팅 ID가 없습니다.');

  // 1) 관련 베팅 내역 조회 (서브콜렉션)
  const topicRef = firestore
    .collection(customCollectionPath || CollectionPath.BETTING)
    .doc(topicId);
  const betsSnap = await topicRef.collection('bets').get();

  // 2) 일괄 삭제 (500 한도 준수)
  let batch = firestore.batch();
  let opCount = 0;
  const commitBatch = async () => {
    if (opCount > 0) {
      await batch.commit();
      batch = firestore.batch();
      opCount = 0;
    }
  };

  for (const doc of betsSnap.docs) {
    const bet = doc.data() as IMyBettingBet;
    // 삭제: betting/{topicId}/bets/{userId}
    batch.delete(topicRef.collection('bets').doc((bet as any).userId));
    opCount++;

    // 삭제: users/{uid}/bettings/{betId}
    if (bet?.userId) {
      batch.delete(
        firestore
          .collection(CollectionPath.USERS)
          .doc(bet.userId)
          .collection('bettings')
          .doc(doc.id),
      );
      opCount++;
    }

    if (opCount >= 450) {
      await commitBatch();
    }
  }

  // 커밋 남은 작업
  await commitBatch();

  // 3) 주제 문서 삭제
  await firestore
    .collection(customCollectionPath || CollectionPath.BETTING)
    .doc(topicId)
    .delete();
}

export async function setBettingAnswer({
  topicId,
  correctKey,
  announceAt,
  customCollectionPath,
}: {
  topicId: string;
  correctKey: string;
  announceAt?: number;
  customCollectionPath?: string;
}) {
  const ref = firestore
    .collection(customCollectionPath || CollectionPath.BETTING)
    .doc(topicId);
  await ref.set(
    { correctKey, announceAt: announceAt || Date.now(), updatedAt: Date.now() },
    { merge: true },
  );
}

// Bets -------------------------------------------------------------------
export async function placeUserBet({
  topicId,
  userId,
  optionKey,
  amount,
}: {
  topicId: string;
  userId: string;
  optionKey: string;
  amount: number;
}) {
  const betKey = `${topicId}__${userId}`;
  const topicRef = firestore.collection(CollectionPath.BETTING).doc(topicId);
  const topicBetRef = topicRef.collection('bets').doc(userId);

  // 재참여 방지: 이미 서브콜렉션에 존재하면 에러
  const existingSub = await topicBetRef.get();
  if (existingSub.exists) {
    throw new Error('이미 참여한 베팅입니다.');
  }

  const betDoc: IMyBettingBet = {
    id: betKey,
    topicId,
    userId,
    optionKey,
    amount: Math.max(0, Number(amount || 0)),
    claimed: false,
    createdAt: Date.now(),
  };

  // 사용자 서브 콜렉션에 저장: users/{uid}/bettings/{betKey}
  const userBetRef = firestore
    .collection(CollectionPath.USERS)
    .doc(userId)
    .collection('bettings')
    .doc(betKey);

  // 주제 bets 서브콜렉션 + participants 업데이트

  await firestore.runTransaction(async (tx) => {
    const topicSnap = await tx.get(topicRef);
    if (!topicSnap.exists) throw new Error('베팅 항목이 존재하지 않습니다.');

    // participants 중복 확인
    const current =
      (topicSnap.data()?.participants as Array<{
        userId: string;
        bettingAmount: number;
      }>) || [];
    if (current.some((p) => p.userId === userId)) {
      throw new Error('이미 참여한 베팅입니다.');
    }

    // 신규 구조: betting/{topicId}/bets/{userId}
    tx.set(topicBetRef, betDoc);

    tx.set(
      userBetRef,
      {
        topicId,
        userId,
        betId: betKey,
        optionKey,
        amount: betDoc.amount,
        createdAt: betDoc.createdAt,
      },
      { merge: true },
    );
    tx.set(
      topicRef,
      {
        participants: firebase.firestore.FieldValue.arrayUnion({
          userId,
          bettingAmount: betDoc.amount,
        }),
        updatedAt: Date.now(),
      },
      { merge: true },
    );
  });

  // 베팅 금액 차감
  if (betDoc.amount > 0) {
    try {
      await updateUserWallet({
        uids: [userId],
        price: -betDoc.amount, // 차감 (음수)
        type: WalletHistoryType.BETTING,
        memo: '베팅 참여',
      });
    } catch (e) {
      console.error('베팅 금액 차감 실패:', e);
      // 차감 실패 시 롤백이 필요할 수 있음
      throw new Error('베팅 금액 차감에 실패했습니다.');
    }
  }

  return betKey;
}

export async function cancelUserBet({
  topicId,
  userId,
}: {
  topicId: string;
  userId: string;
}) {
  const betKey = `${topicId}__${userId}`;
  const topicRef = firestore.collection(CollectionPath.BETTING).doc(topicId);
  const topicBetRef = topicRef.collection('bets').doc(userId);
  const userBetRef = firestore
    .collection(CollectionPath.USERS)
    .doc(userId)
    .collection('bettings')
    .doc(betKey);

  let refundAmount: number | undefined;

  await firestore.runTransaction(async (tx) => {
    const [betSnapSub, userBetSnap, topicSnap] = await Promise.all([
      tx.get(topicBetRef),
      tx.get(userBetRef),
      tx.get(topicRef),
    ]);

    const betFromSub = betSnapSub.exists
      ? (betSnapSub.data() as IMyBettingBet)
      : undefined;
    const userBetData = userBetSnap.exists
      ? (userBetSnap.data() as Partial<IMyBettingBet>)
      : undefined;
    const topic = topicSnap.data() as IBettingItem | undefined;

    // 두 위치 모두에서 데이터를 찾지 못한 경우
    if (!betFromSub && !userBetData) {
      throw new Error('베팅 내역이 없습니다.');
    }

    // claimed 체크 (두 위치 중 하나라도 확인)
    const isClaimed = betFromSub?.claimed || userBetData?.claimed;
    if (isClaimed) throw new Error('이미 정산된 베팅입니다.');

    if (topic?.announceAt && Date.now() >= (topic.announceAt as number)) {
      throw new Error('마감 이후에는 취소할 수 없습니다.');
    }

    // participants 정리 --------------------------------------------------
    // 1) 정확 매칭으로 제거 시도 (금액 확보된 경우)
    const amount = betFromSub?.amount ?? userBetData?.amount;
    refundAmount = amount;
    if (typeof amount === 'number') {
      const participantObj = { userId, bettingAmount: amount };
      tx.set(
        topicRef,
        {
          participants:
            firebase.firestore.FieldValue.arrayRemove(participantObj),
          updatedAt: Date.now(),
        },
        { merge: true },
      );
    } else {
      // 2) fallback: 전체 participants에서 userId 일치 항목 필터링해 덮어쓰기
      const current = (
        (topic?.participants as Array<{
          userId: string;
          bettingAmount: number;
        }>) || []
      ).filter((p) => String(p.userId).trim() !== String(userId).trim());
      tx.set(
        topicRef,
        {
          participants: current,
          updatedAt: Date.now(),
        },
        { merge: true },
      );
    }

    // 문서 삭제 ----------------------------------------------------------
    if (betSnapSub.exists) tx.delete(topicBetRef);
    if (userBetSnap.exists) tx.delete(userBetRef);
  });

  // 베팅 금액 환불 (취소 시)
  if (typeof refundAmount === 'number' && refundAmount > 0) {
    try {
      await updateUserWallet({
        uids: [userId],
        price: refundAmount, // 환불
        type: WalletHistoryType.BETTING,
        memo: '베팅 취소 환불',
      });
    } catch (e) {
      console.error('베팅 환불 실패:', e);
    }
  }
}

export async function claimBetPayout({
  topic,
  bet,
}: {
  topic: IBettingItem;
  bet: IMyBettingBet;
}) {
  if (!topic?.correctKey || !bet) return; // 정답인지
  if (bet.claimed) return; // 이미 정산된 베팅입니다.

  const isWin = bet.optionKey === topic.correctKey;
  const payout = isWin ? bet.amount * 2 : -bet.amount;
  console.log({ isWin, payout });

  try {
    // 지갑 반영 (계산 페이지의 정산 방식과 동일)
    await updateUserWallet({
      uids: [bet.userId],
      price: payout,
      type: WalletHistoryType.BETTING,
      memo: topic.title || '',
    });
    // 히스토리도 함께 기록 (베팅 메모 태깅)
    await saveWalletHistory({
      uids: [bet.userId],
      data: { price: payout, memo: '베팅 정산' },
    });
  } catch (e) {}

  const topicRef = firestore
    .collection(CollectionPath.BETTING)
    .doc(bet.topicId);
  const topicBetRef = topicRef.collection('bets').doc(bet.userId);

  // 신규 구조: 서브콜렉션에 claimed 반영
  await topicBetRef.set({ claimed: true }, { merge: true });

  // 사용자 서브컬렉션에도 claimed 반영 (UI가 해당 경로를 구독함)
  await firestore
    .collection(CollectionPath.USERS)
    .doc(bet.userId)
    .collection('bettings')
    .doc(bet.id as string)
    .set({ claimed: true }, { merge: true });

  return { isWin, payout };
}
